import { Box, Divider } from '@rbilabs/universal-components';

import { IPrices, IVendorConfig } from '@rbi-ctg/menu';
import { useStoreContext } from 'state/store';
import { pluIsAvailable } from 'utils/availability/menu-availability';
import { centsToDollarString } from 'utils/index';
import { PluTypes, concatenateSizePlu } from 'utils/vendor-config';

import { CopyButton } from './copy-button';

const PRICE_NOT_AVAILABLE = 'Not Available';

const PriceRow = Box.withConfig({
  flexDirection: 'row',
  gap: '$4',
  justifyContent: 'space-between',
});

const PriceInfo = ({
  price,
  pluIsAvailable,
}: {
  price: number | undefined;
  pluIsAvailable: boolean;
}) => {
  const priceToString = centsToDollarString(price);
  const priceToCopy = pluIsAvailable ? priceToString : PRICE_NOT_AVAILABLE;
  const priceLabel = pluIsAvailable ? `Price: $${priceToString}` : `Price: ${PRICE_NOT_AVAILABLE}`;
  return <CopyButton label={priceLabel} textToCopy={priceToCopy} variant="copyOne" />;
};

const PluInfoDetails = ({
  vendorConfig,
  prices,
}: {
  vendorConfig: IVendorConfig;
  prices: IPrices;
}) => {
  switch (vendorConfig.pluType) {
    case PluTypes.CONSTANT:
      const constantPluIsAvailable = pluIsAvailable(vendorConfig.constantPlu, prices);
      return (
        <PriceRow>
          <CopyButton
            label={`${vendorConfig.pluType}: ${vendorConfig.constantPlu}`}
            textToCopy={vendorConfig.constantPlu}
            variant="copyOne"
          />
          <PriceInfo
            price={prices?.[vendorConfig.constantPlu]}
            pluIsAvailable={constantPluIsAvailable}
          />
        </PriceRow>
      );
    case PluTypes.IGNORE:
      return <Box>{`PLU: ignore`}</Box>;
    case PluTypes.MULTI_CONSTANT:
      let pluIsAvailableInPrices: boolean = false;
      return (
        <>
          {(vendorConfig.multiConstantPlus || []).map(({ plu }) => {
            pluIsAvailableInPrices = pluIsAvailable(plu, prices);
            return (
              <>
                <CopyButton key={plu} label={`PLU: ${plu}`} textToCopy={plu} />
                <PriceInfo price={prices?.[plu]} pluIsAvailable={pluIsAvailableInPrices} />
                <Divider />
              </>
            );
          })}
        </>
      );
    case PluTypes.PARENT_CHILD:
      const { plu: parentPlu, childPlu } = vendorConfig.parentChildPlu;
      const parentChildPlu = `${parentPlu}-${childPlu}`;

      const parentPluIsAvailable = pluIsAvailable(parentPlu, prices);
      const childPluIsAvailable = pluIsAvailable(childPlu, prices);
      const parentChildPluIsAvailable = pluIsAvailable(parentChildPlu, prices);

      return (
        <>
          <PriceRow>
            <CopyButton label={`Parent PLU: ${parentPlu}`} textToCopy={parentPlu} />
            <PriceInfo price={prices?.[parentPlu]} pluIsAvailable={parentPluIsAvailable} />
          </PriceRow>
          <PriceRow>
            <CopyButton label={`Child PLU: ${childPlu}`} textToCopy={childPlu} />
            <PriceInfo price={prices?.[childPlu]} pluIsAvailable={childPluIsAvailable} />
          </PriceRow>
          <PriceRow>
            <CopyButton label={`ParentChild PLU: ${parentChildPlu}`} textToCopy={parentChildPlu} />
            <PriceInfo
              price={prices?.[parentChildPlu]}
              pluIsAvailable={parentChildPluIsAvailable}
            />
          </PriceRow>
        </>
      );
    case PluTypes.QUANTITY:
      let quantityPluAvailable: boolean = false;
      return (
        <>
          {(vendorConfig.quantityBasedPlu || []).map(({ plu, quantity }) => {
            quantityPluAvailable = pluIsAvailable(plu, prices);
            return (
              <>
                <PriceRow>
                  <CopyButton
                    key={plu}
                    label={`PLU: ${plu} - QUANTITY: ${quantity}`}
                    textToCopy={plu}
                  />
                  <PriceInfo price={prices?.[plu]} pluIsAvailable={quantityPluAvailable} />
                </PriceRow>
                <Divider />
              </>
            );
          })}
        </>
      );
    case PluTypes.SIZE_BASED:
      const sizeBasedPlu = concatenateSizePlu(vendorConfig.sizeBasedPlu) ?? '';
      const sizeBasedPluIsAvailable = pluIsAvailable(sizeBasedPlu, prices);
      return (
        <PriceRow>
          <CopyButton
            label={`${vendorConfig.pluType}: ${sizeBasedPlu}`}
            textToCopy={sizeBasedPlu}
          />
          <PriceInfo price={prices?.[sizeBasedPlu]} pluIsAvailable={sizeBasedPluIsAvailable} />
        </PriceRow>
      );
    default:
      return null;
  }
};

export const PluInfo = ({ vendorConfig }: { vendorConfig: IVendorConfig }) => {
  const { prices } = useStoreContext();

  return <PluInfoDetails vendorConfig={vendorConfig} prices={prices} />;
};
